// @ts-check
import { SETTINGS_ICON_SVG } from '../../shared/icons.js';

import { escapeHtml } from './html.js';

/**
 * Per-table column visibility.
 *
 * Every admin table carries more columns than any one job needs, and the wide ones
 * (Users at ten columns) push Actions off the side. Rather than choosing a fixed set
 * for everyone, each table gets a Columns menu whose selection is remembered.
 *
 * Hidden columns are stored by *header label* rather than index, so renaming or
 * reordering a column resets that one entry instead of silently hiding its neighbour.
 * Hiding is applied as a generated stylesheet rather than per-cell styles, because
 * every table re-renders its `tbody` on each refresh and would otherwise lose it.
 */

/** localStorage key holding `{ [tableKey]: string[] }`. */
export const COLUMN_STORAGE_KEY = 'puzzle_admin_hidden_columns';

/** Minimum columns a table must keep, so a menu cannot empty its own table. */
export const MIN_VISIBLE_COLUMNS = 1;

/**
 * Columns hidden on a fresh visit (before the operator customises the chooser).
 *
 * Secondary identifiers and rarely-scanned fields stay off by default so wide
 * tables remain usable; every label is still offered in the Columns menu.
 *
 * @type {Record<string, string[]>}
 */
export const DEFAULT_HIDDEN_COLUMNS = {
  images: ['ID', 'Description', 'Updated'],
  rooms: ['Creator', 'Completed', 'Guild', 'Channel', 'Deleted'],
  users: ['User ID', 'Discord ID'],
  flags: ['Flag ID', 'Sender', 'Updated'],
  errors: ['Fingerprint', 'User ID', 'App version', 'Client IP'],
  messages: ['Created', 'Updated'],
  bot: ['Instance ID', 'Claimed', 'Announce', 'Completed', 'Room activity']
};

/**
 * Hidden labels for one table: saved preference when present, otherwise defaults.
 *
 * @param {Record<string, string[]>} state
 * @param {string} tableKey
 * @returns {string[]}
 */
export function hiddenLabelsForTable(state, tableKey) {
  if (state && Object.prototype.hasOwnProperty.call(state, tableKey)) {
    return Array.isArray(state[tableKey]) ? state[tableKey].map((v) => String(v)) : [];
  }
  return (DEFAULT_HIDDEN_COLUMNS[tableKey] || []).slice();
}

/**
 * Reads the saved selection, tolerating absent or corrupt storage.
 *
 * @param {any} [storage] - Defaults to `localStorage`.
 * @returns {Record<string, string[]>}
 */
export function readHiddenColumns(storage) {
  const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
  if (!store) return {};
  try {
    const parsed = JSON.parse(store.getItem(COLUMN_STORAGE_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    /** @type {Record<string, string[]>} */
    const out = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (Array.isArray(value)) out[key] = value.map((v) => String(v));
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Persists the selection. A storage failure is not worth interrupting the operator.
 *
 * @param {Record<string, string[]>} state
 * @param {any} [storage] - Defaults to `localStorage`.
 * @returns {void}
 */
export function writeHiddenColumns(state, storage) {
  const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
  if (!store) return;
  try {
    store.setItem(COLUMN_STORAGE_KEY, JSON.stringify(state || {}));
  } catch {
    // Private mode or a full quota: the menu still works for this session.
  }
}

/**
 * Visible header text. A `<br>` becomes a space, so "Players" / "(Active/Total)"
 * stays one label in the Columns menu instead of `Players(Active/Total)`.
 *
 * Uses DOM nodes and textContent instead of innerText so CSS text-transform
 * (e.g. uppercase on table headers) does not alter the label casing in the chooser.
 *
 * @param {any} th
 * @returns {string}
 */
export function headerLabel(th) {
  if (!th) return '';
  const nodes = th.childNodes;
  if (nodes && typeof nodes.length === 'number' && nodes.length > 0) {
    let out = '';
    for (const node of Array.from(nodes)) {
      out += node && node.nodeName === 'BR' ? ' ' : String(node?.textContent || '');
    }
    return out.replace(/\s+/g, ' ').trim();
  }
  return String(th.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Resolves hidden labels to the 1-based column positions `nth-child` needs.
 *
 * @param {string[]} headers - Header labels in table order.
 * @param {string[]} hiddenLabels
 * @returns {number[]}
 */
export function hiddenColumnIndexes(headers, hiddenLabels) {
  const hidden = new Set((hiddenLabels || []).map((label) => String(label)));
  const indexes = [];
  headers.forEach((label, i) => {
    if (hidden.has(String(label))) indexes.push(i + 1);
  });
  // Never hide the last remaining column, whatever storage says.
  if (headers.length - indexes.length < MIN_VISIBLE_COLUMNS) {
    return indexes.slice(0, Math.max(0, headers.length - MIN_VISIBLE_COLUMNS));
  }
  return indexes;
}

/**
 * Builds the stylesheet that hides the given columns of one table.
 *
 * @param {string} scopeSelector - Selector for the table's container.
 * @param {number[]} indexes - 1-based column positions.
 * @returns {string}
 */
export function columnStyleCss(scopeSelector, indexes) {
  if (!scopeSelector || !indexes || indexes.length === 0) return '';
  const selectors = indexes
    .map((n) => `${scopeSelector} th:nth-child(${n}), ${scopeSelector} td:nth-child(${n})`)
    .join(', ');
  return `${selectors} { display: none; }`;
}

/**
 * Renders the checkbox list inside a chooser menu.
 *
 * @param {string} tableKey
 * @param {string[]} headers
 * @param {string[]} hiddenLabels
 * @returns {string}
 */
export function columnMenuHtml(tableKey, headers, hiddenLabels) {
  const hidden = new Set((hiddenLabels || []).map((label) => String(label)));
  const items = headers.map((label, i) => {
    const id = `col-${tableKey}-${i}`;
    return (
      '<label class="column-option" for="' + id + '">' +
        '<input type="checkbox" id="' + id + '" data-column-label="' + escapeHtml(label) + '"' +
          (hidden.has(label) ? '' : ' checked') + ' />' +
        '<span>' + escapeHtml(label) + '</span>' +
      '</label>'
    );
  }).join('');

  return (
    '<details class="column-chooser">' +
      '<summary class="btn btn-secondary btn-sm" title="Choose which columns this table shows" aria-label="Columns">' + SETTINGS_ICON_SVG + '</summary>' +
      '<div class="column-chooser-panel" role="group" aria-label="Visible columns">' +
        items +
        '<button type="button" class="btn btn-secondary btn-sm column-reset-btn">Show all</button>' +
      '</div>' +
    '</details>'
  );
}

/**
 * Wires every `[data-column-chooser]` mount in the document.
 *
 * Each mount names its table key and the container selector to scope the generated
 * CSS to; headers are read from that table so a new column needs no registration.
 *
 * @param {any} [doc] - Defaults to `document`.
 * @returns {void}
 */
export function initColumnChoosers(doc) {
  const root = doc || (typeof document !== 'undefined' ? document : null);
  if (!root || typeof root.querySelectorAll !== 'function') return;

  const state = readHiddenColumns();
  const mounts = Array.from(root.querySelectorAll('[data-column-chooser]'));

  mounts.forEach((mount) => {
    const tableKey = String(mount.getAttribute('data-column-chooser') || '');
    const scopeSelector = String(mount.getAttribute('data-column-scope') || '');
    if (!tableKey || !scopeSelector) return;

    const headers = Array.from(root.querySelectorAll(`${scopeSelector} thead th`))
      .map((th) => headerLabel(th))
      .filter((label) => label.length > 0);
    if (headers.length === 0) return;

    const styleEl = root.createElement('style');
    styleEl.setAttribute('data-column-style', tableKey);
    root.head?.appendChild(styleEl);

    const apply = () => {
      const indexes = hiddenColumnIndexes(headers, hiddenLabelsForTable(state, tableKey));
      styleEl.textContent = columnStyleCss(scopeSelector, indexes);
    };

    mount.innerHTML = columnMenuHtml(tableKey, headers, hiddenLabelsForTable(state, tableKey));
    apply();

    mount.addEventListener('change', (/** @type {any} */ e) => {
      const input = e.target;
      const label = input?.getAttribute?.('data-column-label');
      if (!label) return;
      const hidden = new Set(hiddenLabelsForTable(state, tableKey));
      if (input.checked) hidden.delete(label);
      else hidden.add(label);

      // Refuse the toggle that would leave nothing to read.
      if (headers.length - hidden.size < MIN_VISIBLE_COLUMNS) {
        input.checked = true;
        return;
      }
      state[tableKey] = Array.from(hidden);
      writeHiddenColumns(state);
      apply();
    });

    mount.addEventListener('click', (/** @type {any} */ e) => {
      if (!e.target?.classList?.contains('column-reset-btn')) return;
      state[tableKey] = [];
      writeHiddenColumns(state);
      mount.querySelectorAll('input[data-column-label]').forEach((/** @type {any} */ input) => {
        input.checked = true;
      });
      apply();
    });
  });
}
